import api from './api';
import { getRedirectUrl } from '../utils/urlHelper';

export interface QRCode {
  id: number;
  user_id: number;
  name: string;
  description: string | null;
  destination_url: string;
  shortcode: string;
  size: number;
  color_dark: string;
  color_light: string;
  error_correction: 'L' | 'M' | 'Q' | 'H';
  margin: number;
  module_style: 'square' | 'rounded' | 'dots' | 'extra-rounded';
  logo_path: string | null;
  logo_size: number;
  scan_count: number;
  created_at: string;
  updated_at: string;
  is_active: boolean;
}

export interface QRCodeCreateData {
  name: string;
  description?: string;
  destination_url: string;
  shortcode?: string;
  size?: number;
  color_dark?: string;
  color_light?: string;
  error_correction?: 'L' | 'M' | 'Q' | 'H';
  margin?: number;
  module_style?: 'square' | 'rounded' | 'dots' | 'extra-rounded';
  logo?: File;
  logo_size?: number;
}

export interface QRCodeUpdateData {
  name?: string;
  description?: string;
  destination_url?: string;
  size?: number;
  color_dark?: string;
  color_light?: string;
  error_correction?: 'L' | 'M' | 'Q' | 'H';
  margin?: number;
  module_style?: 'square' | 'rounded' | 'dots' | 'extra-rounded';
  logo?: File;
  logo_size?: number;
}

export interface QRCodeListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'ASC' | 'DESC';
}

export interface QRCodeListResponse {
  success: boolean;
  data: QRCode[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface QRCodeResponse {
  success: boolean;
  data: QRCode;
  message?: string;
}

export interface QRCodeStatistics {
  total_qrcodes: number;
  total_scans: number;
  avg_scans_per_qrcode: string;
  max_scans: number;
}

export interface ShortcodeCheckResponse {
  success: boolean;
  data: {
    shortcode: string;
    available: boolean;
  };
}

class QRCodeService {
  /**
   * Create a new QR code
   */
  async create(data: QRCodeCreateData): Promise<QRCodeResponse> {
    const formData = new FormData();

    formData.append('name', data.name);
    formData.append('destination_url', data.destination_url);

    if (data.description) formData.append('description', data.description);
    if (data.shortcode) formData.append('shortcode', data.shortcode);
    if (data.size) formData.append('size', data.size.toString());
    if (data.color_dark) formData.append('color_dark', data.color_dark);
    if (data.color_light) formData.append('color_light', data.color_light);
    if (data.error_correction) formData.append('error_correction', data.error_correction);
    if (data.margin !== undefined) formData.append('margin', data.margin.toString());
    if (data.module_style) formData.append('module_style', data.module_style);
    if (data.logo) formData.append('logo', data.logo);
    if (data.logo_size) formData.append('logo_size', data.logo_size.toString());

    const response = await api.post<QRCodeResponse>('/qrcodes', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Get all QR codes (with pagination and search)
   */
  async getAll(params?: QRCodeListParams): Promise<QRCodeListResponse> {
    const response = await api.get<QRCodeListResponse>('/qrcodes', { params });
    return response.data;
  }

  /**
   * Get single QR code by ID
   */
  async getById(id: number): Promise<QRCodeResponse> {
    const response = await api.get<QRCodeResponse>(`/qrcodes/${id}`);
    return response.data;
  }

  /**
   * Update QR code
   */
  async update(id: number, data: QRCodeUpdateData): Promise<QRCodeResponse> {
    const formData = new FormData();

    if (data.name) formData.append('name', data.name);
    if (data.description !== undefined) formData.append('description', data.description);
    if (data.destination_url) formData.append('destination_url', data.destination_url);
    if (data.size) formData.append('size', data.size.toString());
    if (data.color_dark) formData.append('color_dark', data.color_dark);
    if (data.color_light) formData.append('color_light', data.color_light);
    if (data.error_correction) formData.append('error_correction', data.error_correction);
    if (data.margin !== undefined) formData.append('margin', data.margin.toString());
    if (data.module_style) formData.append('module_style', data.module_style);
    if (data.logo) formData.append('logo', data.logo);
    if (data.logo_size) formData.append('logo_size', data.logo_size.toString());

    const response = await api.put<QRCodeResponse>(`/qrcodes/${id}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  /**
   * Delete QR code
   */
  async delete(id: number): Promise<{ success: boolean; message: string }> {
    const response = await api.delete<{ success: boolean; message: string }>(`/qrcodes/${id}`);
    return response.data;
  }

  /**
   * Generate QR code image URL
   * @deprecated Use getImageBlobUrl() for secure image loading instead
   * This method is kept for backward compatibility but exposes tokens in URLs
   *
   * ⚠️ WARNING: This method exposes authentication tokens in URLs which is a security risk.
   * Use getImageBlobUrl() instead for secure blob-based image loading.
   */
  getImageUrl(id: number, format: 'png' | 'svg' | 'dataurl' = 'png'): string {
    console.warn(
      '⚠️ DEPRECATED: qrcodeService.getImageUrl() is deprecated and insecure. ' +
      'Use getImageBlobUrl() instead for secure blob-based image loading.'
    );
    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const token = localStorage.getItem('auth_token');
    return `${baseURL}/qrcodes/${id}/generate?format=${format}&token=${token}`;
  }

  /**
   * Download QR code image using authenticated API call
   */
  async downloadImage(id: number, format: 'png' | 'svg' = 'png'): Promise<void> {
    const response = await api.get(`/qrcodes/${id}/generate?format=${format}`, {
      responseType: 'blob'
    });

    // Create blob URL and trigger download
    const blob = new Blob([response.data], {
      type: format === 'png' ? 'image/png' : 'image/svg+xml'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `qrcode-${id}.${format}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get QR code image as blob URL for preview
   */
  async getImageBlobUrl(id: number, format: 'png' | 'svg' = 'png'): Promise<string> {
    const response = await api.get(`/qrcodes/${id}/generate?format=${format}`, {
      responseType: 'blob'
    });

    const blob = new Blob([response.data], {
      type: format === 'png' ? 'image/png' : 'image/svg+xml'
    });
    return window.URL.createObjectURL(blob);
  }

  /**
   * Get QR code as data URL
   */
  async getDataURL(id: number): Promise<string> {
    const response = await api.get<{ success: boolean; data: { dataURL: string } }>(
      `/qrcodes/${id}/generate?format=dataurl`
    );
    return response.data.data.dataURL;
  }

  /**
   * Get QR code statistics
   */
  async getStatistics(): Promise<QRCodeStatistics> {
    const response = await api.get<{ success: boolean; data: QRCodeStatistics }>('/qrcodes/stats');
    return response.data.data;
  }

  /**
   * Check if shortcode is available
   */
  async checkShortcode(shortcode: string): Promise<boolean> {
    const response = await api.get<ShortcodeCheckResponse>(
      `/qrcodes/check-shortcode/${shortcode}`
    );
    return response.data.data.available;
  }

  /**
   * Get redirect URL for shortcode
   * Uses centralized URL helper for consistent production URLs
   */
  getRedirectUrl(shortcode: string): string {
    return getRedirectUrl(shortcode);
  }
}

// Export singleton instance
export const qrcodeService = new QRCodeService();
export default qrcodeService;
